// General API endpoints
import apiClient from './apiClient';
import type { TagCategory, VenueFeature, CareerPosition, GeneralContactRequest } from '@/types/api';

export type HomeStats = {
  members?: number;
  venues?: number;
  cities?: number;
};

// Small helper to unwrap API responses similar to venuesApi.unwrapResponse
function unwrapIfNeeded<T>(response: unknown): T {
  if (response === null || response === undefined) return response as T;
  if (typeof response !== 'object') return response as T;
  const obj = response as Record<string, unknown>;
  if ('data' in obj && obj.data !== undefined) {
    // If nested data object contains another data, prefer that
    const inner = obj.data as Record<string, unknown>;
    if (inner && typeof inner === 'object' && 'data' in inner) {
      return (inner.data as unknown) as T;
    }
    return (obj.data as unknown) as T;
  }
  return response as T;
}

export const generalApi = {
  /**
   * Get all venue tags for filtering
   */
  getVenueTags: async (): Promise<TagCategory> => {
    const raw = await apiClient.get('/venue/tags');
    return unwrapIfNeeded<TagCategory>(raw);
  },

  /**
   * Get venue features for registration
   */
  getVenueFeatures: async (): Promise<VenueFeature[]> => {
    return apiClient.get('/venue/features');
  },

  /**
   * Get career positions
   */
  getCareerPositions: async (): Promise<CareerPosition[]> => {
    return apiClient.get('/venue/positions');
  },

  /**
   * Send general contact form
   */
  sendContact: async (data: GeneralContactRequest): Promise<{ message: string }> => {
    return apiClient.post('/contact', data);
  },

  /**
   * Get platform statistics for home page
   */
  getStats: async (): Promise<HomeStats> => {
    try {
      return await apiClient.get('/stats');
    } catch (error) {
      // Return default stats if endpoint doesn't exist yet
      return {
        members: 10000,
        venues: 0,
        cities: 50
      };
    }
  },
};

export default generalApi;
